import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { DOSE_PLACE_LABELS, eatDayOf, formatIsoDay, type DosePlace } from '@wodi/domain';
import type { ChildHealthView, ImmunisationDoseView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  DateField,
  LoadingBlock,
  Notice,
  PageHeader,
  RadioGroup,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useRef, useState } from 'react';

import {
  childHealthQuery,
  immunisationInError,
  maternityKeys,
  recordGrowth,
  recordImmunisation,
} from '../api/maternity.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { eatInstant } from '../imaging/format.ts';
import { formatInstant, formatIsoDate } from '../patients/format.ts';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { REASON_MIN_LENGTH } from '../patients/reason-dialog.tsx';
import { toasts } from '../toasts.ts';
import { GrowthChart } from './charts.tsx';
import {
  errorText,
  InErrorDialog,
  readWhole,
  saveErrorText,
  useWhen,
  WhenFields,
} from './common.tsx';
import styles from './maternity.module.css';
import { VaccinePicker } from './vaccine-picker.tsx';

// Child health (docs/plans/phase2-maternity-mch.md §1.1 items 9–10, §6): immunisations given
// here (batch from the immunisation store, FEFO unless chosen with a reason) or copied from
// the card, the facility's schedule (or "not set up"), and weight and height as entered with
// no growth reference.

const route = getRouteApi('/_app/patients/$patientId/child-health');
const FEFO = 'fefo';

function DoseForm({ view }: { view: ChildHealthView }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [visitId, setVisitId] = useState(view.openVisits[0]?.id ?? '');
  // Chosen by typing, never preselected (M7).
  const [vaccineId, setVaccineId] = useState('');
  const [dose, setDose] = useState('1');
  const [where, setWhere] = useState<DosePlace>(view.store === null ? 'elsewhere' : 'here');
  const [batch, setBatch] = useState(FEFO);
  const [reason, setReason] = useState('');
  const [cardDay, setCardDay] = useState<string | null>(null);
  const [cardBatch, setCardBatch] = useState('');
  const [site, setSite] = useState('');
  const [note, setNote] = useState('');
  const when = useWhen();
  const requestId = useRef(crypto.randomUUID());
  const doseNumber = readWhole(dose);
  const batches = view.store?.stock.find((s) => s.vaccineId === vaccineId)?.batches ?? [];
  const here = where === 'here';
  const firstBatch = batches[0]?.batchId;
  const chosen = batch !== FEFO && batch !== firstBatch;
  const invalid =
    visitId === '' ||
    vaccineId === '' ||
    doseNumber === null ||
    doseNumber === 'invalid' ||
    (here && (view.store === null || when.invalid)) ||
    (here && chosen && reason.trim().length < REASON_MIN_LENGTH) ||
    // The card's batch is copied when the card has one (M3).
    (!here && cardDay === null);
  const save = useMutation({
    mutationFn: () => {
      const cardAt = cardDay === null ? null : eatInstant(cardDay, '12:00');
      return recordImmunisation(visitId, {
        clientRequestId: requestId.current,
        vaccineId,
        doseNumber: doseNumber === null || doseNumber === 'invalid' ? 1 : doseNumber,
        ...(here
          ? when.value === undefined
            ? {}
            : { givenAt: when.value }
          : cardAt === null
            ? {}
            : { givenAt: cardAt.toISOString() }),
        givenWhere: where,
        batchId: here && batch !== FEFO ? batch : null,
        batchOverrideReason: here && chosen ? reason.trim() : null,
        batchNumberAsEntered: here || cardBatch.trim() === '' ? null : cardBatch.trim(),
        site: site.trim() === '' ? null : site.trim(),
        note: note.trim() === '' ? null : note.trim(),
      });
    },
    onSuccess: (next) => {
      queryClient.setQueryData(maternityKeys.childHealth(view.patientId), next);
      requestId.current = crypto.randomUUID();
      setBatch(FEFO);
      setReason('');
      setCardBatch('');
      setSite('');
      setNote('');
      toasts.show({ title: 'Dose recorded' });
    },
    onError: () => {
      requestId.current = crypto.randomUUID();
    },
  });
  if (view.vaccines.length === 0) {
    return (
      <p className={styles.meta}>
        No vaccines are set up. They are chosen from the catalogue in Settings &gt; Maternity and
        MCH.
      </p>
    );
  }
  if (view.openVisits.length === 0) {
    return (
      <p className={styles.meta}>A dose is recorded on an open visit. Check the child in first.</p>
    );
  }
  const saveError = saveErrorText(save.error, 'The dose was not recorded. Try again.');
  return (
    <form
      className={styles.form}
      aria-label="Record dose"
      onSubmit={(event) => {
        event.preventDefault();
        if (online && !invalid) save.mutate();
      }}
    >
      <div className={styles.when}>
        <VaccinePicker
          vaccines={view.vaccines}
          value={vaccineId}
          onChange={(id) => {
            setVaccineId(id);
            setBatch(FEFO);
          }}
        />
        <TextField
          label="Dose number"
          width="xs"
          inputMode="numeric"
          value={dose}
          onChange={setDose}
          {...(doseNumber === 'invalid' ? { errorMessage: 'A whole number.' } : {})}
        />
        <Select
          label="Visit"
          width="md"
          options={view.openVisits.map((v) => ({
            id: v.id,
            label: `${v.visitNumber}, ${formatInstant(v.startedAt)}`,
          }))}
          value={visitId}
          onChange={(v) => {
            setVisitId(String(v));
          }}
        />
      </div>
      <RadioGroup
        label="Where"
        orientation="horizontal"
        value={where}
        onChange={(v) => {
          setWhere(v as DosePlace);
        }}
        options={[
          { id: 'here', label: DOSE_PLACE_LABELS.here },
          { id: 'elsewhere', label: DOSE_PLACE_LABELS.elsewhere },
        ]}
      />
      {here ? (
        view.store === null ? (
          <Notice tone="info">
            No immunisation store is set up, so a dose can't be taken from stock. Ask for it in
            Settings &gt; Maternity and MCH, or record a dose given elsewhere.
          </Notice>
        ) : (
          <>
            <WhenFields label="Given" when={when} />
            <Select
              label={`Batch from ${view.store.name}`}
              width="md"
              options={[
                { id: FEFO, label: 'Earliest expiry first' },
                ...batches.map((b) => ({
                  id: b.batchId,
                  label: `${b.batchNumber}, expires ${b.expiryDate === null ? 'not set' : formatIsoDate(b.expiryDate)}, ${String(b.quantity)} left`,
                })),
              ]}
              value={batch}
              onChange={(v) => {
                setBatch(String(v));
              }}
              {...(batches.length === 0
                ? { description: 'No stock of this vaccine in the store.' }
                : {})}
            />
            {chosen ? (
              <TextField label="Why not the earliest expiry?" value={reason} onChange={setReason} />
            ) : null}
          </>
        )
      ) : (
        <div className={styles.when}>
          <DateField
            label="Date on the card"
            value={cardDay}
            onChange={setCardDay}
            maxValue={formatIsoDay(eatDayOf(new Date()))}
            {...(view.birthDate === null ? {} : { minValue: view.birthDate })}
          />
          <TextField
            label="Batch on the card"
            description="If the card has one."
            width="sm"
            value={cardBatch}
            onChange={setCardBatch}
          />
        </div>
      )}
      <TextField label="Site" width="sm" value={site} onChange={setSite} />
      <TextArea label="Note" value={note} onChange={setNote} rows={2} />
      {saveError === null ? null : <Notice tone="error">{saveError}</Notice>}
      <div className={styles.actions}>
        <Button
          type="submit"
          variant="primary"
          isDisabled={!online || invalid}
          isPending={save.isPending}
          pendingLabel="Saving…"
        >
          Record dose
        </Button>
      </div>
    </form>
  );
}

function Doses({ view, mayRecord }: { view: ChildHealthView; mayRecord: boolean }) {
  const queryClient = useQueryClient();
  const [marking, setMarking] = useState<ImmunisationDoseView | null>(null);
  const mark = useMutation({
    mutationFn: (reason: string) => immunisationInError(marking?.id ?? '', reason),
    onSuccess: (next) => {
      queryClient.setQueryData(maternityKeys.childHealth(view.patientId), next);
      toasts.show({ title: 'Dose entered in error' });
      setMarking(null);
    },
  });
  return (
    <>
      <Table flat dense aria-label="Immunisations">
        <TableHeader>
          <Column isRowHeader>Vaccine</Column>
          <Column>Dose</Column>
          <Column>Given</Column>
          <Column>Batch</Column>
          <Column>State</Column>
        </TableHeader>
        <TableBody items={view.doses} renderEmptyState={() => 'No doses recorded yet.'}>
          {(d) => (
            <Row id={d.id}>
              <Cell>{d.vaccine.display}</Cell>
              <Cell>{String(d.doseNumber)}</Cell>
              <Cell>
                <span className={styles.numeric}>{formatInstant(d.givenAt)}</span>
                {` ${DOSE_PLACE_LABELS[d.givenWhere].toLowerCase()}${d.storeName === null ? '' : `, ${d.storeName}`}${d.charged ? ', charged' : ''}`}
              </Cell>
              <Cell>
                <span className="wodi-id">{d.batchNumber ?? 'Not recorded'}</span>
              </Cell>
              <Cell>
                {d.status === 'entered_in_error' ? (
                  <Tag state="neutral">Entered in error</Tag>
                ) : mayRecord ? (
                  <Button
                    variant="quiet"
                    dense
                    onPress={() => {
                      setMarking(d);
                    }}
                  >
                    Enter in error
                  </Button>
                ) : (
                  <Tag state="done">Recorded</Tag>
                )}
              </Cell>
            </Row>
          )}
        </TableBody>
      </Table>
      {marking === null ? null : (
        <InErrorDialog
          what="this dose"
          onClose={() => {
            setMarking(null);
          }}
          onConfirm={(reason) => {
            mark.mutate(reason);
          }}
          pending={mark.isPending}
          error={mark.error}
        />
      )}
    </>
  );
}

function DueList({ view }: { view: ChildHealthView }) {
  const due = view.dueList;
  if (due.state === 'not_configured') {
    return (
      <p className={styles.meta}>
        Immunisation schedule not set up. The clinical lead enters it in Settings &gt; Maternity and
        MCH.
      </p>
    );
  }
  if (due.state === 'no_birth_date') {
    return (
      <p className={styles.meta}>No date of birth is recorded, so due dates can't be worked out.</p>
    );
  }
  return (
    <Table flat dense aria-label="Due list">
      <TableHeader>
        <Column isRowHeader>Vaccine</Column>
        <Column>Dose</Column>
        <Column>Due on</Column>
        <Column>State</Column>
      </TableHeader>
      <TableBody
        items={due.entries.map((e) => ({ ...e, id: `${e.vaccine.id}-${String(e.doseNumber)}` }))}
      >
        {(e) => (
          <Row id={e.id}>
            <Cell>{e.vaccine.display}</Cell>
            <Cell>{String(e.doseNumber)}</Cell>
            <Cell>{formatIsoDate(e.dueOn)}</Cell>
            <Cell>
              <Tag
                state={e.state === 'given' ? 'done' : e.state === 'due' ? 'attention' : 'pending'}
              >
                {e.state === 'given' ? 'Given' : e.state === 'due' ? 'Due' : 'Upcoming'}
              </Tag>
            </Cell>
          </Row>
        )}
      </TableBody>
    </Table>
  );
}

function GrowthForm({ view }: { view: ChildHealthView }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [visitId, setVisitId] = useState(view.openVisits[0]?.id ?? '');
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const requestId = useRef(crypto.randomUUID());
  const save = useMutation({
    mutationFn: () =>
      recordGrowth(visitId, {
        clientRequestId: requestId.current,
        weightKg: weight.trim() === '' ? null : weight.trim(),
        heightCm: height.trim() === '' ? null : height.trim(),
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(maternityKeys.childHealth(view.patientId), next);
      requestId.current = crypto.randomUUID();
      setWeight('');
      setHeight('');
      toasts.show({ title: 'Weight and height saved' });
    },
    onError: () => {
      requestId.current = crypto.randomUUID();
    },
  });
  if (view.openVisits.length === 0) return null;
  const empty = weight.trim() === '' && height.trim() === '';
  return (
    <form
      className={styles.form}
      aria-label="Record weight and height"
      onSubmit={(event) => {
        event.preventDefault();
        if (online && !empty && visitId !== '') save.mutate();
      }}
    >
      <div className={styles.when}>
        <Select
          label="Visit"
          width="md"
          options={view.openVisits.map((v) => ({
            id: v.id,
            label: `${v.visitNumber}, ${formatInstant(v.startedAt)}`,
          }))}
          value={visitId}
          onChange={(v) => {
            setVisitId(String(v));
          }}
        />
        <TextField
          label="Weight"
          unit="kg"
          width="sm"
          inputMode="decimal"
          value={weight}
          onChange={setWeight}
        />
        <TextField
          label="Height or length"
          unit="cm"
          width="sm"
          inputMode="decimal"
          value={height}
          onChange={setHeight}
        />
      </div>
      {save.error === null ? null : (
        <Notice tone="error">{errorText(save.error, 'Nothing was saved. Try again.')}</Notice>
      )}
      <div className={styles.actions}>
        <Button
          type="submit"
          isDisabled={!online || empty}
          isPending={save.isPending}
          pendingLabel="Saving…"
        >
          Save weight and height
        </Button>
        <span className={styles.meta}>
          Checked against the possible limits in the clinical rules.
        </span>
      </div>
    </form>
  );
}

export function ChildHealthPage() {
  const { patientId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const child = useQuery(childHealthQuery(patientId));
  if (session === null || !can(session, 'mch.read')) {
    return (
      <Notice tone="info">
        You don't have permission to see child health records. Ask an administrator if you need it.
      </Notice>
    );
  }
  const mayRecord = can(session, 'mch.record');
  return (
    <div className={styles.page}>
      <PatientBannerLoader patientId={patientId} />
      <PageHeader
        title="Child health"
        actions={
          <nav className={styles.links} aria-label="Related">
            <Link to="/patients/$patientId" params={{ patientId }}>
              Patient record
            </Link>
          </nav>
        }
      />
      {online ? null : (
        <Notice tone="info">No connection to Wodi. Saving waits for the connection.</Notice>
      )}
      {child.isPending ? <LoadingBlock label="Loading child health" rows={6} /> : null}
      {child.isError ? (
        <Notice
          tone="error"
          action={<Button onPress={() => void child.refetch()}>Try again</Button>}
        >
          {errorText(child.error, 'Child health could not be loaded. Try again.')}
        </Notice>
      ) : null}
      {child.isSuccess ? (
        <>
          <Section title="Immunisations">
            {mayRecord ? <DoseForm view={child.data} /> : null}
            <Doses view={child.data} mayRecord={mayRecord} />
          </Section>
          <Section title="Due list">
            <DueList view={child.data} />
          </Section>
          <Section title="Growth" description="Weight and height as entered, from any visit.">
            {mayRecord ? <GrowthForm view={child.data} /> : null}
            <GrowthChart label="Weight" unit="kg" points={child.data.growth.weight} />
            <GrowthChart label="Height or length" unit="cm" points={child.data.growth.height} />
          </Section>
        </>
      ) : null}
    </div>
  );
}
